"use client";

import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { HomeTestimonial } from "@/types/home-messages";

import TestimonialAttribution from "./TestimonialAttribution";
import { getTestimonialRange, nextTestimonialIndex, splitTestimonialQuote } from "./testimonialCarousel";

export default function TestimonialsSection() {
  const t = useTranslations("Testimonials");
  const items = (t.raw("items") as HomeTestimonial[]) ?? [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [columns, setColumns] = useState(3);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const tablet = window.matchMedia("(min-width: 768px)");
    const update = () => {
      const count = desktop.matches ? 3 : tablet.matches ? 2 : 1;
      setColumns(count);
      setCurrentIndex((index) => getTestimonialRange(items.length, count, index).start);
    };
    update();
    desktop.addEventListener("change", update);
    tablet.addEventListener("change", update);
    return () => {
      desktop.removeEventListener("change", update);
      tablet.removeEventListener("change", update);
    };
  }, [items.length]);

  if (items.length === 0) return null;

  const { start, end, maxStart } = getTestimonialRange(items.length, columns, currentIndex);
  const move = (direction: -1 | 1) => setCurrentIndex((index) =>
    nextTestimonialIndex(getTestimonialRange(items.length, columns, index).start, maxStart, direction),
  );
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      move(event.key === "ArrowLeft" ? -1 : 1);
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      setCurrentIndex(event.key === "Home" ? 0 : maxStart);
    }
  };

  return (
    <section
      aria-labelledby="home-testimonials-title"
      aria-roledescription="carousel"
      className="landing-testimonials bg-white py-10 md:py-16"
      onKeyDown={onKeyDown}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mx-auto mb-8 max-w-4xl text-center">
          <p className="mb-3 landing-text-detail font-bold uppercase tracking-[0.14em] text-[#FF6B35]">
            {t("eyebrow")}
          </p>
          <h2 className="text-3xl font-bold tracking-tight text-[#0A2B47] sm:text-4xl" id="home-testimonials-title">
            {t("title")}
          </h2>
          <p className="landing-testimonial-support mx-auto mt-4 max-w-3xl text-[#0A2B47]">
            {t.rich("subtitle", { brand: (text) => <span className="font-semibold">{text}</span> })}
          </p>
        </header>

        <div
          aria-label={t("carouselLabel")}
          className="landing-testimonials-window focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0A2B47]"
          id="home-testimonial-window"
          role="group"
          tabIndex={0}
          onTouchStart={(event) => {
            touchStart.current = event.touches.length === 1
              ? { x: event.touches[0].clientX, y: event.touches[0].clientY }
              : null;
          }}
          onTouchCancel={() => { touchStart.current = null; }}
          onTouchEnd={(event) => {
            const from = touchStart.current;
            touchStart.current = null;
            const to = event.changedTouches[0];
            if (!from || !to) return;
            const dx = to.clientX - from.x;
            const dy = to.clientY - from.y;
            if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.2) move(dx < 0 ? 1 : -1);
          }}
        >
          <div className="landing-testimonials-track" style={{ "--testimonial-index": start } as CSSProperties}>
            {items.map((testimonial, index) => {
              const quote = splitTestimonialQuote(testimonial.content, testimonial.emphasis);
              return (
                <article
                  aria-hidden={index < start || index >= end}
                  aria-label={t("slideLabel", { current: index + 1, total: items.length })}
                  aria-roledescription="slide"
                  className="h-full min-w-0"
                  key={`${testimonial.attribution}-${index}`}
                >
                  <Card className="h-full gap-0 rounded-2xl border-[#ECECF0] bg-white p-6 shadow-[0_2px_8px_rgba(10,43,71,0.04)] lg:p-7">
                    <Quote aria-hidden="true" className="mb-5 h-8 w-8 shrink-0 text-[#FF6B35]" strokeWidth={1.6} />
                    <blockquote className="landing-testimonial-quote flex-1 text-[#0A2B47]" lang={testimonial.language}>
                      {quote.before}
                      {quote.emphasis ? <strong className="font-semibold">{quote.emphasis}</strong> : null}
                      {quote.after}
                    </blockquote>
                    <footer className="mt-6 border-t border-[#ECECF0] pt-5">
                      <TestimonialAttribution {...testimonial} />
                    </footer>
                  </Card>
                </article>
              );
            })}
          </div>
        </div>

        {maxStart > 0 ? (
          <nav aria-label={t("navigationLabel")} className="mt-6 flex items-center justify-center gap-2">
            <Button
              aria-controls="home-testimonial-window"
              aria-label={t("previous")}
              className="size-11 shrink-0 rounded-full border-[#CFDDE9] bg-white text-[#0A2B47] hover:bg-[#EEF4F9] hover:text-[#0A2B47] focus-visible:ring-[#0A2B47] motion-reduce:transition-none"
              onClick={() => move(-1)} size="icon" type="button" variant="outline"
            >
              <ChevronLeft aria-hidden="true" className="h-5 w-5" />
            </Button>
            <div aria-label={t("positionControls")} className="flex" role="group">
              {Array.from({ length: maxStart + 1 }, (_, index) => (
                <button
                  aria-controls="home-testimonial-window"
                  aria-current={index === start ? "true" : undefined}
                  aria-label={t("goToPage", { from: index + 1, to: Math.min(index + columns, items.length), total: items.length, count: Math.min(columns, items.length) })}
                  className="flex size-11 shrink-0 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0A2B47]"
                  key={index} onClick={() => setCurrentIndex(index)} type="button"
                >
                  <span aria-hidden="true" className={`h-1.5 rounded-full ${index === start ? "w-5 bg-[#FF6B35]" : "w-1.5 bg-[#CFDDE9]"}`} />
                </button>
              ))}
            </div>
            <Button
              aria-controls="home-testimonial-window"
              aria-label={t("next")}
              className="size-11 shrink-0 rounded-full border-[#CFDDE9] bg-white text-[#0A2B47] hover:bg-[#EEF4F9] hover:text-[#0A2B47] focus-visible:ring-[#0A2B47] motion-reduce:transition-none"
              onClick={() => move(1)} size="icon" type="button" variant="outline"
            >
              <ChevronRight aria-hidden="true" className="h-5 w-5" />
            </Button>
          </nav>
        ) : null}
        <p aria-live="polite" aria-atomic="true" className="sr-only">
          {t("position", { from: start + 1, to: end, total: items.length, count: end - start })}
        </p>
      </div>
    </section>
  );
}
